// Session Detail's grid: four measured columns and two flexible ones.
//
// A column must fit the wider of its header and its cells. The numbers below
// are the cell measurements from the design frame (4729:69708); what a header
// needs is derived from the header itself, so renaming a column re-measures it
// instead of clipping the new label.

// The header's own furniture, from reportTableStyles.js and StudentReports.js:
// 8px of padding either side of the cell, an 8px gap, and the 24px sort icon.
// The icon does not shrink (`flex-shrink: 0`), so it is the label that gets
// pushed out of the cell when there is not enough room.
const HEADER_PADDING_X = 8;
const SORT_CONTROL_GAP = 8;
const SORT_CONTROL_WIDTH = 24;

// What a sort control costs the label beside it. The two Headline columns
// carry none, so their headers are charged the padding and nothing else.
export const SORT_CONTROL_SPACE = SORT_CONTROL_GAP + SORT_CONTROL_WIDTH;

export const HEADER_CHROME_WIDTH = HEADER_PADDING_X * 2 + SORT_CONTROL_SPACE;

// The header's type, from ColumnHeader: Inter Bold 16/22.
const HEADER_FONT_SIZE = 16;

// Advance widths for Inter, as a fraction of the font size, BY GLYPH CLASS and
// rounded UP — every class here is at least as wide as the widest glyph in it
// at Bold. They describe the FONT, not any column, which is why a rename
// re-measures instead of going stale.
//
// Rounding up is deliberate: a width that is a little too generous costs the
// two flexible columns a few pixels, bounded by their three-line clamp, while
// one that is a little too small clips a header.
const WIDE_GLYPHS = "MWmw";
const NARROW_GLYPHS = "IiJjLlTtFfr.,:;'!|()[]";
const ADVANCE_EM = {
  wide: 0.95,
  narrow: 0.42,
  space: 0.3,
  digit: 0.64,
  upper: 0.72,
  other: 0.62,
};

const advanceOf = (character) => {
  if (character === " ") return ADVANCE_EM.space;
  if (WIDE_GLYPHS.includes(character)) return ADVANCE_EM.wide;
  if (NARROW_GLYPHS.includes(character)) return ADVANCE_EM.narrow;
  if (character >= "0" && character <= "9") return ADVANCE_EM.digit;
  if (character === character.toUpperCase() && character !== character.toLowerCase()) {
    return ADVANCE_EM.upper;
  }
  return ADVANCE_EM.other;
};

/** How wide a run of text is, in pixels, at the header's own type. */
export const textWidth = (text, fontSize = HEADER_FONT_SIZE) =>
  [...String(text)].reduce((total, character) => total + advanceOf(character) * fontSize, 0);

/**
 * The longest word a header cannot break.
 *
 * The header cell wraps between words (SessionTable sets `white-space: normal`
 * on its headers), so "Collaboration Level" may take two lines and the column
 * does not have to hold the whole label. It DOES have to hold the longest
 * single word: a word with nowhere to break is what overflowed into the
 * neighbouring column.
 */
export const longestUnbreakableWord = (label) =>
  String(label)
    .split(/\s+/)
    .reduce((longest, word) => (textWidth(word) > textWidth(longest) ? word : longest), "");

/**
 * What a header needs to draw its label, and its sort control, inside its own
 * column.
 *
 * `sortable: false` is for a header that offers no control — the two Headlines.
 * No allowance is charged for a control that does not exist.
 *
 * The glyph model rounds up because jsdom cannot measure text, so the table's
 * floor may slightly exceed the width at which every header truly fits; that
 * only shows a scrollbar a little early. Under-stating is never acceptable: it
 * paints a header over its neighbour.
 */
export const headerWidthNeededBy = (label, { sortable = true } = {}) =>
  Math.ceil(
    HEADER_PADDING_X * 2 +
      (sortable ? SORT_CONTROL_SPACE : 0) +
      textWidth(longestUnbreakableWord(label)),
  );

// The six columns, left to right. `cellWidth` is the design frame's own
// measurement of what the values need (4729:69708). Under `table-layout: fixed`
// a <col> with no width takes the remainder, so the two `flexible` Headlines
// absorb every pixel the four do not need, at any viewport.
export const SESSION_COLUMNS = [
  { label: "Session Date", cellWidth: 105 },
  { label: "Group Focus", cellWidth: 170 },
  { label: "Solo Accuracy", cellWidth: 120 },
  { label: "Math Headline", flexible: true },
  { label: "Collaboration Level", cellWidth: 150 },
  { label: "Collaboration Headline", flexible: true },
];

/** What one column needs: the wider of its own cells and its own header. */
export const columnNeedOf = (column) =>
  column.flexible
    ? headerWidthNeededBy(column.label, { sortable: false })
    : Math.max(column.cellWidth, headerWidthNeededBy(column.label));

export const SESSION_COLUMN_WIDTHS = SESSION_COLUMNS.map((column) =>
  column.flexible ? null : columnNeedOf(column),
);

/**
 * The narrowest the table may be drawn: the sum of what its columns need.
 *
 * `Table` is `width: 100%` with no minimum, so without a floor `TableWrapper`'s
 * `overflow-x: auto` never engages and narrow viewports squeeze the two
 * Headlines to nothing. Below the floor the wrapper scrolls; above it nothing
 * changes. It is computed from the columns so that a rename moves it.
 */
export const minWidthOf = (columns) => {
  const flexible = columns.filter((column) => column.flexible);
  const spoken = columns
    .filter((column) => !column.flexible)
    .reduce((total, column) => total + columnNeedOf(column), 0);

  // The flexible pair needs twice the larger need, not the sum of the two:
  // under `table-layout: fixed` columns with no width "equally divide the
  // remaining horizontal table space" (CSS 2.2 section 17.5.2.1).
  return spoken + flexible.length * Math.max(...flexible.map(columnNeedOf));
};

export const SESSION_TABLE_MIN_WIDTH = minWidthOf(SESSION_COLUMNS);
